import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FaArrowRight, FaArrowDown } from "react-icons/fa";
import { BsArrowLeftRight } from "react-icons/bs";

const QualityTraceability = () => {
  const { t } = useTranslation();

  const allSteps = [
    {
      image: "/images/about/Organic Farmer.png",
      title: t("qualityTraceability.steps.organicFarmer.title"),
      subtitle: t("qualityTraceability.steps.organicFarmer.subtitle"),
      iconBg: "bg-green-100 dark:bg-green-900/30",
    },
    {
      image: "/images/about/Supplier.png",
      title: t("qualityTraceability.steps.supplier.title"),
      subtitle: t("qualityTraceability.steps.supplier.subtitle"),
      iconBg: "bg-blue-100 dark:bg-blue-900/30",
    },
    {
      image: "/images/about/Raw Materials.png",
      title: t("qualityTraceability.steps.rawMaterials.title"),
      subtitle: t("qualityTraceability.steps.rawMaterials.subtitle"),
      iconBg: "bg-purple-100 dark:bg-purple-900/30",
    },
    {
      image: "/images/about/Factory.png",
      title: t("qualityTraceability.steps.factory.title"),
      subtitle: t("qualityTraceability.steps.factory.subtitle"),
      iconBg: "bg-orange-100 dark:bg-orange-900/30",
    },
    {
      image: "/images/about/Finished Products.png",
      title: t("qualityTraceability.steps.finishedProducts.title"),
      subtitle: t("qualityTraceability.steps.finishedProducts.subtitle"),
      iconBg: "bg-teal-100 dark:bg-teal-900/30",
    },
    {
      image: "/images/about/Buyer.png",
      title: t("qualityTraceability.steps.buyer.title"),
      subtitle: t("qualityTraceability.steps.buyer.subtitle"),
      iconBg: "bg-indigo-100 dark:bg-indigo-900/30",
    },
    {
      image: "/images/about/Retailer.png",
      title: t("qualityTraceability.steps.retailer.title"),
      subtitle: t("qualityTraceability.steps.retailer.subtitle"),
      iconBg: "bg-pink-100 dark:bg-pink-900/30",
    },
    {
      image: "/images/about/Product Label.png",
      title: t("qualityTraceability.steps.productLabel.title"),
      subtitle: t("qualityTraceability.steps.productLabel.subtitle"),
      iconBg: "bg-gray-100 dark:bg-gray-900/30",
    },
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-gray-50 dark:bg-dark-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary-500 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10 items-center">
          {/* Left Side - Flowchart */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 order-2 lg:order-1"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white dark:bg-dark-800 p-4 md:p-6 lg:p-8">
              <div className="grid grid-cols-2 gap-4 md:hidden">
                {allSteps.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                    className="flex flex-col items-center text-center bg-gray-50 dark:bg-dark-700/50 rounded-xl p-3 shadow-sm"
                  >
                    <div
                      className={`${step.iconBg} w-16 h-16 rounded-xl flex items-center justify-center mb-2.5 shadow-md p-2.5`}
                    >
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-full object-contain"
                      />
                    </div>

                    <h4 className="text-xs font-bold text-gray-800 dark:text-white mb-1 leading-tight">
                      {step.title}
                    </h4>

                    {step.subtitle && (
                      <p className="text-[10px] text-gray-600 dark:text-gray-400 line-clamp-2">
                        {step.subtitle}
                      </p>
                    )}

                    <div className="mt-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-[10px] font-semibold px-2 py-0.5 rounded-full">
                      Step {index + 1}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* TABLET: 2 rows x 4 columns */}
              <div className="hidden md:grid lg:hidden grid-cols-4 gap-4">
                {/* Top Row */}
                <div className="col-span-4 flex items-center justify-between gap-3">
                  {allSteps.slice(0, 4).map((step, index) => (
                    <React.Fragment key={index}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className="flex flex-col items-center text-center flex-1"
                      >
                        <div
                          className={`${step.iconBg} w-16 h-16 rounded-xl flex items-center justify-center mb-2 shadow-md p-2`}
                        >
                          <img
                            src={step.image}
                            alt={step.title}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <h4 className="text-xs font-bold text-gray-800 dark:text-white mb-1">
                          {step.title}
                        </h4>
                        {step.subtitle && (
                          <p className="text-[10px] text-gray-600 dark:text-gray-400 line-clamp-2">
                            {step.subtitle}
                          </p>
                        )}
                      </motion.div>
                      {index < 3 && (
                        <BsArrowLeftRight className="text-primary-600 text-xl flex-shrink-0" />
                      )}
                    </React.Fragment>
                  ))}
                </div>

                <div className="col-span-4 flex justify-center my-2">
                  <FaArrowDown className="text-primary-600 dark:text-primary-400 text-2xl" />
                </div>

                {/* Bottom Row */}
                <div className="col-span-4 flex items-center justify-between gap-3">
                  {allSteps.slice(4, 8).map((step, index) => (
                    <React.Fragment key={index}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: (index + 4) * 0.1 }}
                        className="flex flex-col items-center text-center flex-1"
                      >
                        <div
                          className={`${step.iconBg} w-16 h-16 rounded-xl flex items-center justify-center mb-2 shadow-md p-2`}
                        >
                          <img
                            src={step.image}
                            alt={step.title}
                            className="w-full h-full object-contain"
                          />
                        </div>
                        <h4 className="text-xs font-bold text-gray-800 dark:text-white mb-1">
                          {step.title}
                        </h4>
                        {step.subtitle && (
                          <p className="text-[10px] text-gray-600 dark:text-gray-400 line-clamp-2">
                            {step.subtitle}
                          </p>
                        )}
                      </motion.div>
                      {index < 3 && (
                        <BsArrowLeftRight className="text-primary-600 text-xl flex-shrink-0" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* DESKTOP: Original Layout */}
              <div className="hidden lg:block">
                {/* Top Row */}
                <div className="flex items-center justify-between gap-4 mb-16 relative">
                  {allSteps.slice(0, 4).map((step, index) => (
                    <React.Fragment key={index}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.15 }}
                        className="flex flex-col items-center text-center flex-1 relative"
                      >
                        <motion.div
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          className={`${step.iconBg} w-20 h-20 rounded-xl flex items-center justify-center mb-3 shadow-lg p-3`}
                        >
                          <img
                            src={step.image}
                            alt={step.title}
                            className="w-full h-full object-contain"
                          />
                        </motion.div>
                        <h4 className="text-sm font-bold text-gray-800 dark:text-white mb-1 leading-tight">
                          {step.title}
                        </h4>
                        {step.subtitle && (
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {step.subtitle}
                          </p>
                        )}

                        {index === 3 && (
                          <motion.div
                            initial={{ opacity: 0, scaleY: 0 }}
                            whileInView={{ opacity: 1, scaleY: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: 0.8 }}
                            className="absolute -bottom-12 left-1/2 transform -translate-x-1/2"
                          >
                            <BsArrowLeftRight className="text-primary-600 dark:text-primary-400 text-3xl rotate-90" />
                          </motion.div>
                        )}
                      </motion.div>

                      {index < 3 && (
                        <motion.div
                          initial={{ opacity: 0, scaleX: 0 }}
                          whileInView={{ opacity: 1, scaleX: 1 }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 0.4,
                            delay: index * 0.15 + 0.1,
                          }}
                          className="flex items-center justify-center flex-shrink-0"
                        >
                          <BsArrowLeftRight className="text-primary-600 dark:text-primary-400 text-3xl" />
                        </motion.div>
                      )}
                    </React.Fragment>
                  ))}
                </div>

                {/* Bottom Row */}
                <div className="flex items-center justify-between gap-4">
                  {allSteps.slice(4, 8).map((step, index) => (
                    <React.Fragment key={index}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{
                          duration: 0.5,
                          delay: (index + 4) * 0.15,
                        }}
                        className="flex flex-col items-center text-center flex-1"
                      >
                        <motion.div
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          className={`${step.iconBg} w-20 h-20 rounded-xl flex items-center justify-center mb-3 shadow-lg p-3`}
                        >
                          <img
                            src={step.image}
                            alt={step.title}
                            className="w-full h-full object-contain"
                          />
                        </motion.div>
                        <h4 className="text-sm font-bold text-gray-800 dark:text-white mb-1 leading-tight">
                          {step.title}
                        </h4>
                        {step.subtitle && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 max-w-[120px]">
                            {step.subtitle}
                          </p>
                        )}
                      </motion.div>

                      {index < 3 && (
                        <motion.div
                          initial={{ opacity: 0, scaleX: 0 }}
                          whileInView={{ opacity: 1, scaleX: 1 }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 0.4,
                            delay: (index + 4) * 0.15 + 0.1,
                          }}
                          className="flex items-center justify-center flex-shrink-0"
                        >
                          <BsArrowLeftRight className="text-primary-600 dark:text-primary-400 text-3xl" />
                        </motion.div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Traceability Note */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="mt-6 md:mt-8 text-center"
              >
                <p className="text-xs md:text-sm text-primary-600 dark:text-primary-400 font-semibold italic">
                  ← {t("qualityTraceability.traceabilityNote")} →
                </p>
              </motion.div>

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 1 }}
                className="mt-6 md:mt-8 text-center"
              >
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-sm md:text-base shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  {t("qualityTraceability.cta")}
                  <FaArrowRight className="text-sm" />
                </Link>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Side - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-1 order-1 lg:order-2"
          >
            <div className="space-y-6">
              {/* Title */}
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white leading-tight">
                {t("qualityTraceability.title")}
              </h2>

              {/* Description */}
              <div className="text-base md:text-lg text-gray-700 dark:text-gray-300 leading-relaxed space-y-4">
                <p>
                  {t("qualityTraceability.description.part1")}{" "}
                  <span className="font-bold text-primary-600 dark:text-primary-400">
                    {t("qualityTraceability.description.highlight1")}
                  </span>
                  . {t("qualityTraceability.description.part2")}{" "}
                  <span className="font-bold text-primary-600 dark:text-primary-400">
                    {t("qualityTraceability.description.highlight2")}
                  </span>{" "}
                  {t("qualityTraceability.description.part3")}{" "}
                  <span className="font-bold text-primary-600 dark:text-primary-400">
                    {t("qualityTraceability.description.highlight3")}
                  </span>{" "}
                  {t("qualityTraceability.description.part4")}{" "}
                  <span className="font-bold text-primary-600 dark:text-primary-400">
                    {t("qualityTraceability.description.highlight4")}
                  </span>
                  ,{" "}
                  <span className="font-bold text-primary-600 dark:text-primary-400">
                    {t("qualityTraceability.description.highlight5")}
                  </span>
                  , {t("qualityTraceability.description.part5")}{" "}
                  <span className="font-bold text-primary-600 dark:text-primary-400">
                    {t("qualityTraceability.description.highlight6")}
                  </span>{" "}
                  {t("qualityTraceability.description.part6")}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default QualityTraceability;
